import type { IConfigCache } from '../../interface/config';
import type { IFetcher, IFetchRequest } from '../../interface/fetch';
import type { ILog } from '../../interface/log';
import type { IProgressPromiseGenerator } from '../../interface/promise';
import type { IRegexSerializer } from '../../interface/regex';
import { ICallbackMessage } from '../../message/interface/fetchToCache';
import { IMessageInstantiator } from '../../message/interface/instantiator';
import { IChannelController, IChannelStatus } from '../../util/interface/channel';
import {
    IServiceWorkerCallback,
    IServiceWorkerClient,
    IServiceWorkerStatus,
} from '../interface/worker';
import dispatch from './dispatch';
import registration from './registration';
import rules from './rules';

interface IServiceWorkerPrivate extends IServiceWorkerClient {
    registration: ServiceWorkerRegistration | null;
    worker: ServiceWorker | null;
    statusChangeCallbacks: Array<IServiceWorkerCallback>;
}

function _requestInNamedCache(request: Request, cacheName: string): Promise<boolean> {
    return new Promise(function (resolve, reject) {
        caches
            .open(cacheName)
            .then(function (cache) {
                return cache.match(request, {
                    ignoreMethod: true,
                    ignoreSearch: true,
                });
            })
            .then(function (response) {
                const hasCachedValue = response !== undefined;
                resolve(hasCachedValue);
            })
            .catch(error => reject(error));
    });
}

const _notifyCallbacksOfStatusChange = (worker: IServiceWorkerPrivate) => {
    for (const callback of worker.statusChangeCallbacks) {
        callback(worker as IServiceWorkerClient);
    }
};

const _obtainScreenLock = async () =>
    'wakeLock' in navigator
        ? await navigator.wakeLock.request('screen')
        : {
              release: () => {
                  /* noop */
              },
          };

function ServiceWorker(
    dependencies: {
        log: ILog;
        fetcher: IFetcher;
        promiser: IProgressPromiseGenerator;
        config: IConfigCache;
        regexParser: IRegexSerializer;
        message: IMessageInstantiator;
        channelController: IChannelController;
    },
    url: string,
    scope: string,
): IServiceWorkerClient {
    const response: IServiceWorkerPrivate = {
        status: IServiceWorkerStatus.Idle,
        registration: null,
        worker: null,
        statusChangeCallbacks: [],

        isInterceptingNetworkRequests: function (this: IServiceWorkerPrivate) {
            return navigator?.serviceWorker?.controller !== null;
        },

        start: function (this: IServiceWorkerPrivate) {
            const _that = this;

            let hasInstalledRules = false;

            const _installRules = (worker: ServiceWorker) => {
                if (hasInstalledRules) {
                    return;
                }
                hasInstalledRules = true;

                rules(
                    dependencies.log,
                    dependencies.regexParser,
                    dependencies.message,
                    worker,
                    dependencies.config,
                );
            };

            const _onReady = (worker: ServiceWorker) => {
                _that.worker = worker;

                _that.status = IServiceWorkerStatus.Ready;
                _notifyCallbacksOfStatusChange(_that);

                _installRules(worker);

                const checkControllerStatus = () => {
                    _that.status = _that.isInterceptingNetworkRequests()
                        ? IServiceWorkerStatus.Running
                        : IServiceWorkerStatus.Ready;
                    _notifyCallbacksOfStatusChange(_that);
                };

                navigator?.serviceWorker?.addEventListener(
                    'controllerchange',
                    checkControllerStatus,
                );

                checkControllerStatus();

                /* cleanup: unsubscribe & remove event listener when we are no longer in ready state */
                const statusChangeCb = (swCallback: IServiceWorkerClient) => {
                    const shouldUnsubscribe =
                        swCallback.status !== IServiceWorkerStatus.Ready &&
                        swCallback.status !== IServiceWorkerStatus.Running;

                    if (shouldUnsubscribe) {
                        navigator?.serviceWorker?.removeEventListener(
                            'controllerchange',
                            checkControllerStatus,
                        );
                        _that.unsubscribeStatusChange(statusChangeCb);
                    }
                };

                _that.subscribeStatusChange(statusChangeCb);
            };

            const _onInstall = (worker: ServiceWorker) => {
                _installRules(worker);
                _that.worker = worker;
            };

            registration
                .start(dependencies.log, url, {
                    scope,
                    onInstall: _onInstall,
                    onReady: _onReady,
                    onRegister: reg => {
                        _that.registration = reg;
                    },
                })
                .catch(error =>
                    dependencies.log.error(
                        `Failed to start registration: ${JSON.stringify(error)}`,
                    ),
                );

            dependencies.log.info('Starting service-worker');
            this.status = IServiceWorkerStatus.Starting;
            _notifyCallbacksOfStatusChange(this);
        },

        subscribeStatusChange: function (
            this: IServiceWorkerPrivate,
            callback: IServiceWorkerCallback,
        ) {
            this.statusChangeCallbacks.push(callback);
        },

        unsubscribeStatusChange: function (
            this: IServiceWorkerPrivate,
            callback: IServiceWorkerCallback,
        ) {
            this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
        },

        stop: function (this: IServiceWorkerPrivate) {
            if (!this.registration) {
                dependencies.log.error('Worker not registered, skipping stop');
                return;
            }
            this.status = IServiceWorkerStatus.Stopped;
            _notifyCallbacksOfStatusChange(this);

            registration.stop(dependencies.log, this.registration);
        },

        addCacheFirst: function (
            this: IServiceWorkerPrivate,
            matcher: RegExp,
            maxAge: number,
            maxEntries: number,
            cacheName: string,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addCacheFirst');
                return;
            }
            dependencies.log.info(`Registering cache-first: ${cacheName}`);
            dispatch.addCacheFirst(
                { ...dependencies, worker: this.worker },
                matcher,
                maxAge,
                maxEntries,
                cacheName,
            );
        },

        addOAuthTokenCache: function (this: IServiceWorkerPrivate, pathSuffix: string) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addOAuthCache');
                return;
            }
            dependencies.log.info(`Registering oauthTokenCache: ${pathSuffix}`);
            dispatch.addOAuthTokenCache({ ...dependencies, worker: this.worker }, pathSuffix);
        },

        addZipPathFetchResponder: function (
            this: IServiceWorkerPrivate,
            zipPath: string,
            relativeRespondPath: string,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addZipPathFetchResponder');
                return;
            }
            dependencies.log.info(`Registering zipPathResponder: ${zipPath}`);
            dispatch.addZipPathFetchResponder(
                { ...dependencies, worker: this.worker },
                zipPath,
                relativeRespondPath,
            );
        },

        addHeader: function (
            this: IServiceWorkerPrivate,
            matcher: RegExp,
            headerKey: string,
            headerVal: string,
            expiration: Date,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addHeader');
                return;
            }
            dependencies.log.info(`Registering addHeader: ${headerKey}:${headerVal}`);
            dispatch.addHeader(
                { ...dependencies, worker: this.worker },
                matcher,
                headerKey,
                headerVal,
                expiration,
            );
        },

        isRequestCached: function (
            this: IServiceWorkerPrivate,
            request: IFetchRequest,
            cacheName?: string,
        ): Promise<boolean> {
            return new Promise<boolean>(async function (resolve, reject) {
                try {
                    if (window.caches === undefined) {
                        reject('No caches found');
                        return;
                    }

                    const cacheNames = [cacheName];
                    const reqCheck = new Request(request.url, {
                        method: request.method,
                        body: request.body,
                    });

                    const results = await Promise.all(
                        cacheNames.map(name => _requestInNamedCache(reqCheck, cacheName ?? '')),
                    );
                    const anyCacheHits = results.reduce((prev, cur) => prev || cur);

                    resolve(anyCacheHits);
                } catch (error) {
                    reject(error);
                }
            });
        },

        addRequestsToCache: function (
            this: IServiceWorkerPrivate,
            requests: Array<IFetchRequest>,
            options?: {
                cacheName?: string;
            },
        ) {
            const worker = this.worker;
            if (!worker) {
                throw new Error(`No service-worker running, aborting request to cache`);
            }

            return dependencies.promiser.promise<void>(async function (resolve, reject, progress) {
                const screenLock = await _obtainScreenLock().catch(error => undefined);

                const channelID = requests[requests.length - 1].url;

                const channel = dependencies.channelController.channel(channelID);

                const subKey = 'client-callback';

                const fetchDeps = { ...dependencies, worker };

                /* Service Workers are hunted down & killed shortly after being dispatched to. 
                   To keep this from happening, we *must* periodically send a message to the service worker, 
                   this will prevent Chrome (Safari is OK) from pausing execution. 
                   It becomes apparent on larger downloads on Android-Chrome I.e. Orkney-Mainland
                   Attaching Chrome DevTools will *prevent* this issue from occurring too.
                   https://stackoverflow.com/questions/42733835/service-worker-termination-by-a-timeout-timer-was-canceled-because-devtools-is-a 
                */
                const keepAliveTimer = setInterval(() => dispatch.keepAlive(fetchDeps), 5000);

                const cleanup = () => {
                    channel?.removeListener(subKey, 'NewMessage');
                    channel?.removeListener(subKey, 'Error');
                    channel?.removeListener(subKey, 'StatusChanged');

                    screenLock?.release?.();

                    clearInterval(keepAliveTimer);
                };

                channel?.addEventListener('client-callback', 'NewMessage', message => {
                    const payload = message as ICallbackMessage;
                    if (payload.error) {
                        cleanup();
                        reject(payload.error);
                    } else if (payload.done || payload.progress >= 1) {
                        cleanup();
                        resolve();
                    } else if (payload.progress < 1) {
                        progress(payload.progress);
                    }
                });

                channel?.addEventListener(subKey, 'Error', error => {
                    dependencies.log.error(`FetchToCache message failed: ${JSON.stringify(error)}`);
                    cleanup();
                    reject(error);
                });

                channel?.addEventListener(subKey, 'StatusChanged', status => {
                    if (status === IChannelStatus.Expired) {
                        const message = `Message channel expired`;
                        dependencies.log.error(message);
                        cleanup();
                        reject(new Error(message));
                    }
                });

                dispatch.fetchToCache(fetchDeps, requests, options?.cacheName, channelID);
            });
        },
    };

    return response;
}

export default ServiceWorker;
